import React from 'react';
import { Info } from 'lucide-react';
import { EntityPicker } from '../../../components/ui';
import type { EntityPickerOption } from '../../../components/ui';
import { Label } from '../../../components/ui';
import { FieldError, useFieldInvalid } from '../../../components/entity/FieldError.js';
import { listWorkflows } from '../../../generated/api';
import type { WizardState } from './hire-types.js';

interface HireStepWorkflowsProps {
  state: WizardState;
  onChange: (updates: Partial<WizardState>) => void;
}

const loadWorkflowOptions = async (): Promise<EntityPickerOption[]> => {
  const res = await listWorkflows();
  return (res.data?.data ?? []).map((w) => ({
    id: w.id,
    label: w.name,
    description: [w.category, w.description].filter(Boolean).join(' · ') || undefined,
  }));
};

export const HireStepWorkflows: React.FC<HireStepWorkflowsProps> = ({ state, onChange }) => {
  const workflowInvalid = useFieldInvalid('workflowDefinitionId');
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-3 bg-accent/5 border border-accent/20 rounded-lg">
        <Info size={16} className="text-accent-text mt-0.5 shrink-0" />
        <p className="text-sm text-body">
          Workflows define the step-by-step execution plan. Skills are resolved automatically from each step's role.
          Multi-workflow assignment is coming soon — for now, pick the primary workflow.
        </p>
      </div>

      <div>
        <Label hint="optional">Workflow</Label>
        <EntityPicker
          value={state.workflowDefinitionId ?? null}
          onChange={(id) => onChange({ workflowDefinitionId: id })}
          queryKey={['workflow-definitions']}
          loadOptions={loadWorkflowOptions}
          placeholder="Search workflows..."
          emptyMessage="No workflows available"
          invalid={workflowInvalid}
          ariaLabel="Workflow"
        />
        <FieldError name="workflowDefinitionId" />
        <p className="text-xs text-faint mt-1">
          Leave empty to run without a predefined workflow. The daemon will use its agent role's default behavior.
        </p>
      </div>

      <div className="p-4 bg-surface-alt rounded-lg border">
        <h4 className="text-sm font-medium text-heading mb-2">What workflows control</h4>
        <ul className="space-y-1.5 text-xs text-muted">
          <li className="flex items-start gap-2">
            <span className="text-accent-text mt-0.5">1.</span>
            <span>Execution steps — ordered sequence of tasks the daemon performs</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-accent-text mt-0.5">2.</span>
            <span>Skill resolution — each step resolves required skills from the catalog</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-accent-text mt-0.5">3.</span>
            <span>Output routing — where findings, reports, and artifacts are delivered</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-accent-text mt-0.5">4.</span>
            <span>Error handling — retry logic, fallbacks, and escalation paths</span>
          </li>
        </ul>
      </div>
    </div>
  );
};
